/**
 * Desktop first-run intro gate.
 *
 * The Electron shell's main window opens `/start`, which sits behind
 * RequireAuth — without a gate the very first thing a new install shows is a
 * login form. `AuthenticatedApp` consults this module before its auth gate and
 * fronts the one-time intro (`pages/desktop-intro.tsx`) instead. Web builds
 * never show it, and a machine already claimed as a player (stored screen
 * credential) boots straight into its usual flow — a kiosk restart must never
 * stall on a splash.
 *
 * The decision is a pure function so it runs under the Node test glob
 * (src/lib/*.test.ts); the localStorage flag and environment reads live in
 * the small wrappers below.
 */

import { readStoredCredential } from "../player/lib/screen-credential.ts";
import { isDesktopShell } from "./platform.ts";

/** localStorage key recording that the desktop intro was completed. */
export const DESKTOP_INTRO_SEEN_STORAGE_KEY = "omnimedia.desktopIntroSeen.v1";

/** Pure decision: should this render front the desktop intro? */
export function resolveDesktopIntroGate(input: {
  isDesktop: boolean;
  introSeen: boolean;
  hasCredential: boolean;
}): boolean {
  return input.isDesktop && !input.introSeen && !input.hasCredential;
}

function storage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    // Storage access can throw (e.g. hardened contexts) — treat as absent.
    return null;
  }
}

/** Whether the intro was already completed on this install. */
export function hasSeenDesktopIntro(): boolean {
  const store = storage();
  // No storage means no way to remember: fail toward the app, never toward
  // replaying the intro on every launch.
  if (!store) return true;
  try {
    return store.getItem(DESKTOP_INTRO_SEEN_STORAGE_KEY) === "true";
  } catch {
    return true;
  }
}

/** Record that the intro was completed. */
export function markDesktopIntroSeen(): void {
  try {
    storage()?.setItem(DESKTOP_INTRO_SEEN_STORAGE_KEY, "true");
  } catch {
    // Best effort — worst case the (skippable) intro shows once more.
  }
}

/** Environment-reading convenience for the router gate. */
export function shouldShowDesktopIntro(): boolean {
  return resolveDesktopIntroGate({
    isDesktop: isDesktopShell(),
    introSeen: hasSeenDesktopIntro(),
    hasCredential: Boolean(readStoredCredential()),
  });
}
